import { defineStore } from 'pinia';
import { fetchPaymentSnapshots, paymentRequest } from '@/services/supplierPayments';
import axios from 'axios';
import { useAuthStore } from './auth';
import { useJournalAppointmentsStore } from './journalAppointments';
import { useJournalPurchasesStore } from './journalPurchases';
import { useClientInvoicesStore } from './clientInvoices';
import {
	buildAppointmentsSnapshot,
	buildPurchasesSnapshot,
	buildInvoicesSnapshot,
	buildSummaryFromSnapshots,
} from '@/utils/analyticsSnapshot';

const STRAPI_URL = import.meta.env.VITE_STRAPI_URL ?? 'http://localhost:1337';
const DEFAULT_PAGE_SIZE = 200;

const normalizeReport = (entry) => {
	const attributes = entry?.attributes || entry || {};
	const appointmentsSnapshot = Array.isArray(attributes.appointmentsSnapshot)
		? attributes.appointmentsSnapshot
		: [];
	const purchasesSnapshot = Array.isArray(attributes.purchasesSnapshot)
		? attributes.purchasesSnapshot
		: [];
	const invoicesSnapshot = Array.isArray(attributes.invoicesSnapshot)
		? attributes.invoicesSnapshot
		: [];
	const wagePayments = Array.isArray(attributes.wagePayments) ? attributes.wagePayments : [];
	const hasSnapshotData = Boolean(
		appointmentsSnapshot.length ||
		purchasesSnapshot.length ||
		invoicesSnapshot.length ||
		wagePayments.length,
	);
	const computedSummary = hasSnapshotData
		? buildSummaryFromSnapshots({
			appointments: appointmentsSnapshot,
			purchases: purchasesSnapshot,
			invoices: invoicesSnapshot,
			wagePayments,
		})
		: (attributes.summary || null);
	return {
		id: entry?.id ?? attributes.id ?? null,
		documentId: attributes.documentId ?? entry?.documentId ?? null,
		name: attributes.name ?? '',
		dateFrom: attributes.dateFrom ?? null,
		dateTo: attributes.dateTo ?? null,
		status: attributes.status ?? 'draft',
		summary: computedSummary,
		appointmentsSnapshot,
		purchasesSnapshot,
		invoicesSnapshot,
		wagePayments,
		notes: attributes.notes ?? '',
		metadata: attributes.metadata ?? {},
		createdAt: attributes.createdAt ?? null,
		updatedAt: attributes.updatedAt ?? null,
		raw: entry,
	};
};

const sanitizePayload = (payload = {}) => {
	const sanitized = {};
	Object.entries(payload).forEach(([key, value]) => {
		if (value === undefined) return;
		if (Array.isArray(value)) {
			sanitized[key] = value;
			return;
		}
		if (value === '' || value === null) {
			sanitized[key] = null;
			return;
		}
		sanitized[key] = value;
	});
	return sanitized;
};

const fetchInvoicesForList = async (store, listId, filters) => {
	const collected = [];
	let page = 1;
	let pageCount = 1;

	while (page <= pageCount) {
		const { items = [], pagination = {} } = await store.fetchItemsSnapshot(listId, {
			filters,
			page,
			pageSize: DEFAULT_PAGE_SIZE,
			sort: ['invoiceDate:asc', 'invoiceNumber:asc'],
		});
		collected.push(...items);
		pageCount = pagination.pageCount ?? page;
		page += 1;
		if (!pagination.pageCount && items.length < DEFAULT_PAGE_SIZE) {
			break;
		}
	}

	return collected;
};

const waitForLists = async (store, timeoutMs = 5000) => {
	const start = Date.now();
	while (store.listsLoading && !store.lists.length) {
		if (Date.now() - start > timeoutMs) break;
		await new Promise((resolve) => setTimeout(resolve, 50));
	}
};

export const useAnalyticsReportsStore = defineStore('analyticsReports', {
	state: () => ({
		reports: [],
		chartReports: [],
		isLoading: false,
		isChartsLoading: false,
		error: null,
		chartsError: null,
		reportCache: {},
		pagination: {
			page: 1,
			pageSize: 25,
			pageCount: 1,
			total: 0,
		},
	}),
	actions: {
		authHeaders() {
			const authStore = useAuthStore();
			return authStore.token ? authStore.requestHeaders.headers : {};
		},

		// Read the reports without touching the list, so a page can decide whether it is safe to
		// show what changed elsewhere.
		async peekReports({ page = 1, pageSize = 25 } = {}) {
			const response = await axios.get(`${STRAPI_URL}/api/analytics-reports`, {
				headers: this.authHeaders(),
				params: {
					sort: 'updatedAt:desc',
					'pagination[page]': page,
					'pagination[pageSize]': pageSize,
				},
			});
			const entries = response?.data?.data || [];
			const meta = response?.data?.meta?.pagination || {};
			return {
				reports: entries.map(normalizeReport),
				pagination: {
					page: meta.page || page,
					pageSize: meta.pageSize || pageSize,
					pageCount: meta.pageCount || 1,
					total: meta.total || entries.length,
				},
			};
		},

		async peekReportsForCharts({ pageSize = DEFAULT_PAGE_SIZE } = {}) {
			const collected = [];
			let page = 1;
			let pageCount = 1;

			while (page <= pageCount) {
				// eslint-disable-next-line no-await-in-loop
				const response = await axios.get(`${STRAPI_URL}/api/analytics-reports`, {
					headers: this.authHeaders(),
					params: {
						sort: 'updatedAt:desc',
						'pagination[page]': page,
						'pagination[pageSize]': pageSize,
					},
				});
				const entries = response?.data?.data || [];
				const meta = response?.data?.meta?.pagination || {};
				collected.push(...entries);
				pageCount = meta.pageCount ?? page;
				page += 1;
				if (!meta.pageCount && entries.length < pageSize) {
					break;
				}
			}

			return collected.map(normalizeReport);
		},

		async fetchReports({ page = 1, pageSize = 25 } = {}) {
			if (this.isLoading) return;
			this.isLoading = true;
			this.error = null;
			try {
				const { reports, pagination } = await this.peekReports({ page, pageSize });
				this.reports = reports;
				this.pagination = pagination;
				return this.reports;
			} catch (error) {
				this.error =
					error?.response?.data?.error?.message ||
					error?.message ||
					'Failed to load analytics reports';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},

		async fetchReportsForCharts({ pageSize = DEFAULT_PAGE_SIZE } = {}) {
			if (this.isChartsLoading) return this.chartReports;
			this.isChartsLoading = true;
			this.chartsError = null;
			try {
				this.chartReports = await this.peekReportsForCharts({ pageSize });
				return this.chartReports;
			} catch (error) {
				this.chartsError =
					error?.response?.data?.error?.message ||
					error?.message ||
					'Failed to load analytics chart reports';
				throw error;
			} finally {
				this.isChartsLoading = false;
			}
		},

		async fetchReportById(documentId) {
			if (!documentId) return null;
			if (this.reportCache[documentId]) {
				return this.reportCache[documentId];
			}
			const response = await axios.get(`${STRAPI_URL}/api/analytics-reports/${documentId}`, {
				headers: this.authHeaders(),
			});
			const report = normalizeReport(response?.data?.data || {});
			this.reportCache[documentId] = report;
			return report;
		},

		async createReport(payload) {
			const response = await axios.post(
				`${STRAPI_URL}/api/analytics-reports`,
				{ data: sanitizePayload(payload) },
				{ headers: this.authHeaders() },
			);
			const created = normalizeReport(response?.data?.data || {});
			this.reports = [created, ...this.reports];
			if (this.chartReports.length) {
				const alreadyExists = this.chartReports.some(
					(entry) =>
						entry.documentId === created.documentId || String(entry.id) === String(created.id),
				);
				if (!alreadyExists) {
					this.chartReports = [created, ...this.chartReports];
				}
			}
			this.reportCache[created.documentId || created.id] = created;
			return created;
		},

		async updateReport(documentId, payload) {
			if (!documentId) throw new Error('Missing analytics report identifier');
			const response = await axios.put(
				`${STRAPI_URL}/api/analytics-reports/${documentId}`,
				{ data: sanitizePayload(payload) },
				{ headers: this.authHeaders() },
			);
			const updated = normalizeReport(response?.data?.data || {});
			const next = this.reports.map((entry) =>
				entry.documentId === updated.documentId || entry.id === updated.id
					? { ...entry, ...updated }
					: entry,
			);
			this.reports = next;
			if (this.chartReports.length) {
				this.chartReports = this.chartReports.map((entry) =>
					entry.documentId === updated.documentId || entry.id === updated.id
						? { ...entry, ...updated }
						: entry,
				);
			}
			this.reportCache[updated.documentId || updated.id] = updated;
			return updated;
		},

		async deleteReport(documentId) {
			if (!documentId) return false;
			await axios.delete(`${STRAPI_URL}/api/analytics-reports/${documentId}`, {
				headers: this.authHeaders(),
			});
			this.reports = this.reports.filter(
				(entry) => entry.documentId !== documentId && entry.id !== documentId,
			);
			if (this.chartReports.length) {
				this.chartReports = this.chartReports.filter(
					(entry) => entry.documentId !== documentId && entry.id !== documentId,
				);
			}
			delete this.reportCache[documentId];
			return true;
		},

		async createSnapshot({ dateFrom, dateTo } = {}) {
			const appointmentsStore = useJournalAppointmentsStore();
			const purchasesStore = useJournalPurchasesStore();
			const invoicesStore = useClientInvoicesStore();

			if (!invoicesStore.lists.length) {
				await invoicesStore.fetchLists();
				await waitForLists(invoicesStore);
			}

			const [appointments, purchases] = await Promise.all([
				appointmentsStore.queryAppointmentsInRange({
					startDate: dateFrom,
					endDate: dateTo,
					journalName: 'Appointments',
					filters: {
						excludeStatuses: ['cancelled'],
					},
				}),
				purchasesStore.queryPurchasesInRange({
					startDate: dateFrom,
					endDate: dateTo,
				}),
			]);

			const listIds = (invoicesStore.listOptions || []).map((list) => list.id);
			const invoicesByList = await Promise.all(
				listIds.map((listId) =>
					fetchInvoicesForList(invoicesStore, listId, {
						dateFrom: dateFrom || '',
						dateTo: dateTo || '',
					}),
				),
			);
			const invoices = invoicesByList.flat();

			const [settlements, paidResponse] = await Promise.all([fetchPaymentSnapshots(appointments), paymentRequest('/paid', { params: { dateFrom, dateTo } })]);
			const wagePayments = paidResponse.data || [];
			const appointmentsSnapshot = buildAppointmentsSnapshot(appointments, settlements);
			const purchasesSnapshot = buildPurchasesSnapshot(purchases);
			const invoicesSnapshot = buildInvoicesSnapshot(invoices);
			const summary = buildSummaryFromSnapshots({
				appointments: appointmentsSnapshot,
				purchases: purchasesSnapshot,
				invoices: invoicesSnapshot,
				wagePayments,
			});

			return {
				appointmentsSnapshot,
				purchasesSnapshot,
				invoicesSnapshot,
				wagePayments,
				summary,
			};
		},
	},
});
